import { useLayoutEffect, useState } from "react";

/**
 * The rendered width of an element, kept current by a ResizeObserver.
 *
 * ⚠ For the charts: an SVG drawn at a fixed viewBox width inside a wider panel is a small
 * drawing floating in white space (the viewBox aspect IS the rendered aspect — see
 * `GroupedBars`). Hugues, 26 Oct 2026, on the first draft of screen 12: "the graph is too
 * small". Charts are drawn at the width this returns, and redrawn when it changes —
 * including from 0, when a folded panel is opened again.
 *
 * ⚠ Returns a CALLBACK ref, not a ref object: the element usually mounts after the first
 * render (behind a loading state), and an effect keyed on a ref object never hears of it.
 */
export function useElementWidth<T extends HTMLElement>(): [(el: T | null) => void, number] {
  const [element, setElement] = useState<T | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!element) return;
    const measure = () => setWidth(Math.floor(element.clientWidth));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return [setElement, width];
}
